import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentArrowDownIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import api, { templateAPI } from '../../services/api';
import SearchableDropdown from '../common/SearchableDropdown';

const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  
  // PROMPT-043-FIX-07: Role-based entity selection
  const [userRole, setUserRole] = useState('');
  const [entities, setEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [selectedEntityName, setSelectedEntityName] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);

  // PROMPT-043-FIX-07: Fetch user info and entities on modal open
  useEffect(() => {
    if (isOpen) {
      fetchUserInfoAndEntities();
    }
  }, [isOpen]);

  const fetchUserInfoAndEntities = async () => {
    setLoadingEntities(true);
    try {
      // Get user info from localStorage or API
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      const role = userInfo.role || '';
      setUserRole(role);

      // Fetch entities based on role - use user-scoped API for regular users
      let entitiesList = [];
      if (role === 'master_admin' || role === 'MASTER_ADMIN') {
        const response = await api.get('/admin/template-entities');
        entitiesList = response.data?.entities || [];
      } else {
        // Use user-scoped API for regular users
        const response = await api.get('/user/entities');
        entitiesList = Array.isArray(response.data) ? response.data : [];
      }
      
      setEntities(entitiesList);

      // Auto-select if only one entity
      if (entitiesList.length === 1) {
        setSelectedEntityId(entitiesList[0].id.toString());
        setSelectedEntityName(entitiesList[0].entity_name);
      }
    } catch (err) {
      setError('Failed to load entities');
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setUploadResult(null);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await templateAPI.downloadSampleCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_upload_sample.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download sample CSV');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    // PROMPT-043-FIX-07: Validate entity selection
    if (!selectedEntityId) {
      setError('Please select an entity');
      return;
    }

    setUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_id', selectedEntityId); // PROMPT-043-FIX-07: Send entity_id

      const response = await templateAPI.bulkUpload(formData);
      
      setUploadResult(response.data);
      
      if (response.data.success_count > 0) {
        onSuccess();
      }
    } catch (err) {
      // PROMPT-043-FIX-08: Improved error handling with specific messages
      if (err.response) {
        const status = err.response.status;
        if (status === 404) {
          setError('Bulk upload API not found. Please contact administrator.');
        } else if (status === 403) {
          setError('You do not have permission to upload templates.');
        } else if (status === 400) {
          setError(err.response.data?.error || 'Invalid request. Please check your CSV file and entity selection.');
        } else {
          setError(err.response.data?.error || 'Failed to upload templates');
        }
      } else if (err.request) {
        setError('No response from server. Please check your connection.');
      } else {
        setError('Failed to upload templates. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    setError('');
    setSelectedEntityId('');
    setSelectedEntityName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Bulk Upload Templates</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* PROMPT-043-FIX-07: Entity Name Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entity Name *
            </label>
            {entities.length === 0 ? (
              // No entities mapped
              <div>
                <input
                  type="text"
                  value="No entities mapped to your account"
                  readOnly
                  disabled
                  className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg bg-red-50 text-red-700 cursor-not-allowed"
                />
                <p className="text-xs text-red-500 mt-1">
                  Please contact admin to map entities to your account
                </p>
              </div>
            ) : entities.length === 1 ? (
              // Single entity: Show as read-only textbox
              <div>
                <input
                  type="text"
                  value={selectedEntityName}
                  readOnly
                  disabled
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Entity is auto-selected (only one entity mapped)
                </p>
              </div>
            ) : (
              // Multiple entities: Show dropdown
              <div>
                <SearchableDropdown
                  options={entities.map((entity) => ({
                    value: entity.id.toString(),
                    label: entity.entity_name
                  }))}
                  value={selectedEntityId}
                  onChange={(value) => setSelectedEntityId(value)}
                  placeholder="-- Select Entity --"
                  clearable={false}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Selected entity will be applied to all templates in the CSV
                </p>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Instructions</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Download the sample CSV file to see the required format</li>
              <li><strong>CSV must include:</strong> Header, Template ID, Template Name, Template Type, Template Content, Status</li>
              <li><strong>Entity Name:</strong> Controlled by system (not in CSV) - selected above</li>
              <li>Header column can contain multiple headers separated by comma (e.g., HEADER1,HEADER2)</li>
              <li>Status values: Active, Inactive, Pending (case-insensitive)</li>
              <li>Regular users: Status will be forced to Pending regardless of CSV value</li>
              <li>Master Admin: Can set any status value</li>
            </ul>
          </div>

          {/* Download Sample */}
          <div>
            <button
              onClick={handleDownloadSample}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Download Sample CSV
            </button>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
              />
            </div>
            {file && (
              <p className="text-sm text-gray-600 mt-2">
                Selected: <span className="font-medium">{file.name}</span>
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Upload Summary</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{uploadResult.total_rows}</p>
                    <p className="text-xs text-gray-600">Total Rows</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{uploadResult.success_count}</p>
                    <p className="text-xs text-gray-600">Success</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{uploadResult.failed}</p>
                    <p className="text-xs text-gray-600">Failed</p>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              {uploadResult.success_count > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      {uploadResult.success_count} template(s) uploaded successfully
                    </p>
                  </div>
                </div>
              )}

              {/* Error Details */}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-red-900 mb-2">
                    Failed Rows ({uploadResult.errors.length})
                  </h3>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-red-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-red-900">Row</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-red-900">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-200">
                        {uploadResult.errors.map((err, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 text-red-800">{err.row}</td>
                            <td className="px-3 py-2 text-red-700">{err.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || !selectedEntityId || uploading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Uploading...
              </>
            ) : (
              <>
                <ArrowUpTrayIcon className="w-5 h-5" />
                Upload Templates
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
